import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { captureEntitlementId, readEntitlementIdFromSearch } from '@/utils/activation'
import { useActivationEnabled } from './useActivationEnabled'

/**
 * Captures an `entitlementId` arriving in the URL into the activation store
 * (PRO-13). Mounted by AuthActor — every route the id travels through renders
 * that actor — and safe to mount anywhere: with no param in the URL it is a
 * no-op, and re-capture of an id already held is a no-op in the store.
 *
 * Reads `window.location.search` inside the effect rather than using
 * `useSearchParams` (no CSR bailout, nothing storage-derived in a render) and
 * never registers the param with nuqs — this hook only observes the URL, so it
 * cannot collide with AuthActor's `useQueryState('form')` transitions.
 */
export const useEntitlementCapture = (): void => {
  const pathname = usePathname()
  // Cinematic gate (per-scene `options.activation.enabled` on the AuthActor
  // script of the scene matching the current pathname): a route outside the
  // flagged scenes never writes the param to storage at all — see
  // useActivationEnabled.
  const isActivationEnabled = useActivationEnabled()

  // Keyed on pathname only, deliberately: the id arrives via external
  // full-page loads (activation link, reload, firebase redirect), which always
  // mount fresh. A same-pathname *query-only* client navigation would not
  // re-run this effect — no current flow produces one carrying the param, and
  // watching the query would mean useSearchParams and its CSR bailout. If such
  // a flow ever appears, revisit this dependency.
  useEffect(() => {
    if (!isActivationEnabled) return

    const entitlementId = readEntitlementIdFromSearch(window.location.search)

    if (entitlementId) {
      captureEntitlementId(entitlementId)
    }
  }, [pathname, isActivationEnabled])
}
